'use client';

import classnames from 'classnames';
import { forwardRef, useMemo } from 'react';
import {
  getMatchStatus,
  MATCH_EVENT_TYPES,
  MATCH_STATUS,
  MatchEvent,
  OPTA_STATUSES,
  OptaMatchStatus,
  Penalty,
} from '@stadionhq-v3/core';
import { Button } from '@stadionhq-v3/base-ui';
import {
  CardRed,
  CardSecondYellow,
  CardYellow,
  Live,
  OwnGoal,
  SmallFootball,
  Whistle,
} from '@components/icons';
import { Countdown } from '@components/atoms/countdown';
import { SfaMatchStatusPill } from '../../atoms/sfa-match-status-pill';
import { SfaScorerRow } from '../../atoms/sfa-scorer-row';
import { SfaPenaltiesStrip } from '../../molecules/sfa-penalties-strip';
import { SfaPenaltyDotResult } from '../../atoms/sfa-penalty-dot';
import { SfaMatchMeta } from '../../molecules/sfa-match-meta';
import { SfaSponsorPill } from '../../molecules/sfa-sponsor-pill';
import { SfaMatchHeaderProps } from './sfaMatchHeader.types';
import styles from './sfaMatchHeader.module.scss';

const eventIcon = (event: MatchEvent) => {
  switch (event.event) {
    case MATCH_EVENT_TYPES.YELLOW_CARD:
      return <CardYellow />;
    case MATCH_EVENT_TYPES.SECOND_YELLOW:
      return <CardSecondYellow />;
    case MATCH_EVENT_TYPES.RED_CARD:
      return <CardRed />;
    case MATCH_EVENT_TYPES.OWN_GOAL:
      return <OwnGoal />;
    default:
      return <SmallFootball />;
  }
};

const penaltyResults = (
  penalties: Penalty[] | undefined
): SfaPenaltyDotResult[] =>
  (penalties ?? []).map((p) =>
    p && (p as { scored?: boolean }).scored ? 'scored' : 'missed'
  );

const SfaMatchHeader = forwardRef<HTMLDivElement, SfaMatchHeaderProps>(
  (
    {
      nativeProps = {},
      classNames = [],
      testId = 'sfa-match-header',
      venue,
      competitionName,
      kickoffTime,
      date,
      home,
      away,
      matchStatus,
      minute,
      labels = {},
      events,
      penalties,
      referee,
      image,
      ctaLinks,
      countdown,
      fixtureMetaDescription,
      broadcasters,
      sponsorLogo,
    },
    ref
  ) => {
    const normalizedStatus =
      (matchStatus as string) === 'FT'
        ? OPTA_STATUSES.FULL_TIME
        : (matchStatus as OptaMatchStatus);

    const phase = getMatchStatus(normalizedStatus);
    const isPreMatch = phase === MATCH_STATUS.PRE_MATCH;
    const isLive = phase === MATCH_STATUS.LIVE;
    const isPostMatch = phase === MATCH_STATUS.POST_MATCH;

    const phaseClass = isPreMatch
      ? 'sfaMatchHeader--preMatch'
      : isLive
        ? 'sfaMatchHeader--live'
        : 'sfaMatchHeader--postMatch';

    const scoreBoxClass = isLive
      ? styles['scoreBox--live']
      : styles['scoreBox--postMatch'];

    const renderEvents = (
      teamEvents: MatchEvent[] | undefined,
      alignment: 'home' | 'away'
    ) => {
      if (!teamEvents?.length) return null;
      return teamEvents
        .filter((e) =>
          [
            MATCH_EVENT_TYPES.GOAL,
            MATCH_EVENT_TYPES.OWN_GOAL,
            MATCH_EVENT_TYPES.PENALTY_GOAL,
            // Sendings-off: a straight red or a second yellow both dismiss the
            // player, so surface both as red-card events in the header.
            MATCH_EVENT_TYPES.RED_CARD,
            MATCH_EVENT_TYPES.SECOND_YELLOW,
          ].includes(e.event as never)
        )
        .map((event) => {
          // The API already suffixes the minute with an apostrophe (e.g. "45'");
          // strip any trailing apostrophes and re-add exactly one so we never
          // render a double apostrophe, while still covering bare values.
          const time = event.minute
            ? `${event.minute.replace(/'+$/, '')}'`
            : '';
          // Mark penalty goals with a "(P)" suffix, mirroring the football-ui
          // convention (MatchCentreKeyMoment), so they are distinguishable from
          // open-play goals — the shared icon set has no distinct penalty glyph.
          const playerName = `${event.playerName ?? ''}${
            event.event === MATCH_EVENT_TYPES.PENALTY_GOAL ? ' (P)' : ''
          }`;
          // Card icons carry their own red/yellow fills, so they must opt out
          // of the row's monochrome (white) icon recolouring.
          const isCard =
            event.event === MATCH_EVENT_TYPES.RED_CARD ||
            event.event === MATCH_EVENT_TYPES.SECOND_YELLOW ||
            event.event === MATCH_EVENT_TYPES.YELLOW_CARD;
          return (
            <SfaScorerRow
              key={`${alignment}-${event.playerName ?? 'p'}-${event.minute ?? '0'}-${event.event}`}
              alignment={alignment}
              icon={eventIcon(event)}
              time={time}
              playerName={playerName}
              preserveIconColor={isCard}
            />
          );
        });
    };

    const statusPill = useMemo(() => {
      if (isLive) {
        // During a live match show the match clock (e.g. "45'") when available,
        // falling back to the generic "Live" label before the minute streams in.
        const liveLabel = minute?.trim() ? minute : (labels.live ?? 'Live');
        return (
          <SfaMatchStatusPill
            label={liveLabel}
            variant="live"
            icon={<Live />}
          />
        );
      }
      if (isPostMatch) {
        return (
          <SfaMatchStatusPill
            label={labels.fullTime ?? 'Full Time'}
            variant="fullTime"
          />
        );
      }
      return null;
    }, [isLive, isPostMatch, labels.fullTime, labels.live, minute]);

    const centerMetaRows = useMemo(() => {
      const rows: Array<{
        icon?: React.ReactNode;
        label?: string;
        value: string;
      }> = [];
      if (competitionName) rows.push({ value: competitionName });
      if (venue) rows.push({ value: venue });
      if (date) rows.push({ value: date });
      if (kickoffTime && isPreMatch) rows.push({ value: kickoffTime });
      if (referee && (isLive || isPostMatch))
        rows.push({
          icon: <Whistle />,
          label: labels.referee ?? 'Referee:',
          value: referee,
        });
      return rows;
    }, [
      competitionName,
      venue,
      date,
      kickoffTime,
      referee,
      isPreMatch,
      isLive,
      isPostMatch,
      labels.referee,
    ]);

    const homePenalties = penaltyResults(penalties?.home);
    const awayPenalties = penaltyResults(penalties?.away);
    const hasPenalties = homePenalties.length > 0 || awayPenalties.length > 0;

    const ctaButtons = ctaLinks?.filter(Boolean) ?? [];

    const hasAggregate =
      home.aggregateScore != null && away.aggregateScore != null;
    const hasHalfTime =
      isPostMatch &&
      (home.halfTimeScore !== undefined || away.halfTimeScore !== undefined);

    const homeEvents = renderEvents(events?.home, 'home');
    const awayEvents = renderEvents(events?.away, 'away');

    return (
      <div
        {...nativeProps}
        ref={ref}
        data-testid={testId}
        className={classnames(
          styles.sfaMatchHeader,
          styles[phaseClass],
          classNames
        )}
      >
        <div className={styles.heroImage}>
          {image ?? (
            <img
              src="/images/match_header_fallback.jpg"
              alt=""
              loading="lazy"
            />
          )}
        </div>
        <div className={styles.gradient} />

        {fixtureMetaDescription ? (
          <h1 className={styles.hiddenH1}>{fixtureMetaDescription}</h1>
        ) : null}

        <div className={styles.content}>
          {sponsorLogo ? (
            <div className={styles.sponsor}>
              <SfaSponsorPill label="Sponsored by" sponsorLogo={sponsorLogo} />
            </div>
          ) : null}

          <div className={styles.matchUpRow}>
            <div
              className={classnames(
                styles.teamBlock,
                styles['teamBlock--home']
              )}
            >
              <span className={styles.teamName}>{home.clubName ?? ''}</span>
              <div className={styles.crest}>{home.clubCrest}</div>
            </div>

            <div className={styles.scoreBlock}>
              {statusPill ? (
                <div className={styles.scoreBlock__status}>{statusPill}</div>
              ) : null}
              <div className={styles.scoreBoxes}>
                {isPreMatch ? (
                  <span className={styles.vsLabel}>{labels.vs ?? 'vs'}</span>
                ) : (
                  <>
                    <span
                      className={classnames(styles.scoreBox, scoreBoxClass)}
                    >
                      {home.score ?? 0}
                    </span>
                    <span
                      className={classnames(styles.scoreBox, scoreBoxClass)}
                    >
                      {away.score ?? 0}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div
              className={classnames(
                styles.teamBlock,
                styles['teamBlock--away']
              )}
            >
              <div className={styles.crest}>{away.clubCrest}</div>
              <span className={styles.teamName}>{away.clubName ?? ''}</span>
            </div>
          </div>

          {isPreMatch && !!countdown && (
            <Countdown
              {...countdown}
              classNames={[
                classnames(styles.countdownContainer, countdown.classNames),
              ]}
            />
          )}

          {homeEvents ||
          awayEvents ||
          centerMetaRows.length > 0 ||
          hasAggregate ||
          hasHalfTime ? (
            <div className={styles.matchUpDetails}>
              <div
                className={classnames(
                  styles.scorersColumn,
                  styles['scorersColumn--home']
                )}
              >
                {homeEvents}
              </div>
              <div className={styles.centerInfo}>
                {hasAggregate ? (
                  <div className={styles.aggregateRow}>
                    {labels.aggregateAbbreviated ?? 'AGG'}{' '}
                    {home.aggregateScore ?? 0} - {away.aggregateScore ?? 0}
                  </div>
                ) : null}
                {hasHalfTime ? (
                  <div className={styles.halfTimeRow}>
                    {labels.halfTimeAbbreviated ?? 'HT'}{' '}
                    {home.halfTimeScore ?? 0} - {away.halfTimeScore ?? 0}
                  </div>
                ) : null}
                {centerMetaRows.length > 0 ? (
                  <SfaMatchMeta rows={centerMetaRows} />
                ) : null}
              </div>
              <div
                className={classnames(
                  styles.scorersColumn,
                  styles['scorersColumn--away']
                )}
              >
                {awayEvents}
              </div>
            </div>
          ) : null}

          {hasPenalties ? (
            <div className={styles.penalties}>
              <SfaPenaltiesStrip
                home={homePenalties}
                away={awayPenalties}
                separator={labels.penalties ?? 'PENS'}
                homeScore={home.penaltyScore}
                awayScore={away.penaltyScore}
              />
            </div>
          ) : null}

          {broadcasters ? (
            <div className={styles.broadcasters}>{broadcasters}</div>
          ) : null}

          {ctaButtons.length > 0 ? (
            <div className={styles.ctas}>
              {ctaButtons.slice(0, 4).map((cta) => (
                <Button key={`${cta.url ?? ''}-${cta.text ?? ''}`} {...cta} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

SfaMatchHeader.displayName = 'SfaMatchHeader';

export default SfaMatchHeader;
